import { trackInternalEvent } from '@rio/tracking'
import { useMemo } from 'react'
import { useI18n } from 'builder/hooks/useI18n'
import Button, { ButtonTheme } from 'builder/components/Button'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { AbandonmentLocalStorageKeys } from 'builder/modules/ui'
import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors as userSelectors, UserType } from 'builder/modules/user'
import { useLocalStorage } from 'builder/hooks/useLocalStorage'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import ModalOverlay from '../ModalOverlay'
import DrawerModal from '../DrawerModal/DrawerModal'
import RecruitersDesktop from './assets/recruiters-desktop.png'
import RecruitersTablet from './assets/recruiters-tablet.png'
import RecruitersMobile from './assets/recruiters-mobile.png'
import {
  Container,
  Description,
  Title,
  CloseButton,
  Modal,
  RightModalContainer,
  Image,
  Subtitle,
  Items,
  ItemContainer,
  Tick,
  Item,
  ButtonsContainer,
  MobileContent,
} from './styles'

export function useResumeDistributionPromo() {
  const { features } = useFeaturesConfig()
  const userData = useTypedSelector(userSelectors.userData)
  const { resumeDistribution, geoipCode } = features
  const userType = userData?.billingInfo.userType
  const isFlagEnabled = resumeDistribution
  const isEligibleUser = userType === UserType.free || userType === UserType.postPremium
  const [hasSeenPromo, setHasSeenPromo] = useLocalStorage('resume_distribution_promo_seen', false)

  const shouldShowRDPromo = useMemo(() => {
    const isInSupportedRegion = ['US', 'GB'].includes(geoipCode || '')

    return isFlagEnabled && isInSupportedRegion && isEligibleUser && !hasSeenPromo
  }, [geoipCode, isFlagEnabled, isEligibleUser, hasSeenPromo])

  return { shouldShowRDPromo, setHasSeenPromo }
}

/**
 * This is needed because the ResumeDistributionModal CTA's trigger a
 * resume download flow and the AbandonmentModal is a blocker for that flow.
 * So this 2 modals conflict with each other. This solutions makes sure that
 * either the AbandonmentModal or the ResumeDistributionModal is shown.
 */
const bypassAbandonmentModal = () => {
  localStorage.setItem(AbandonmentLocalStorageKeys.SHOW_ABANDONMENT_MODAL_FLAG, 'true')
}

const Content = ({ triggerDownload }: { triggerDownload: () => void }) => {
  const { i18n } = useI18n()

  return (
    <>
      <Title>{i18n.t('builder.resume_distribution_modal.title')}</Title>
      <Subtitle>{i18n.t('builder.resume_distribution_modal.subtitle')}</Subtitle>
      <Items>
        <ItemContainer>
          <Tick />
          <Item>{i18n.t('builder.resume_distribution_modal.item_one')}</Item>
        </ItemContainer>
        <ItemContainer>
          <Tick />
          <Item>{i18n.t('builder.resume_distribution_modal.item_two')}</Item>
        </ItemContainer>
        <ItemContainer>
          <Tick />
          <Item>{i18n.t('builder.resume_distribution_modal.item_three')}</Item>
        </ItemContainer>
        <ItemContainer>
          <Tick />
          <Item>{i18n.t('builder.resume_distribution_modal.item_four')}</Item>
        </ItemContainer>
      </Items>

      <Description>{i18n.t('builder.resume_distribution_modal.description')}</Description>

      <ButtonsContainer>
        <Button
          style={{ flex: 1 }}
          theme={ButtonTheme.ghost}
          onClick={() => {
            trackInternalEvent('click_rd_modal_cta', { cta: 'download_only' })
            sessionStorage.removeItem('redirect_to_resume_distribution')
            bypassAbandonmentModal()
            triggerDownload()
          }}
        >
          {i18n.t('builder.resume_distribution_modal.download_only')}
        </Button>
        <Button
          style={{ flex: 1 }}
          onClick={() => {
            trackInternalEvent('click_rd_modal_cta', { cta: 'download_and_send' })
            sessionStorage.setItem('redirect_to_resume_distribution', 'true')
            bypassAbandonmentModal()
            triggerDownload()
          }}
        >
          {i18n.t('builder.resume_distribution_modal.download_and_send')}
        </Button>
      </ButtonsContainer>
    </>
  )
}

type Props = {
  onClose: () => void
  triggerDownload: () => void
  setHasSeenPromo: (value: boolean) => void
}

export const ResumeDistributionModal = (props: Props) => {
  const { onClose, triggerDownload, setHasSeenPromo } = props
  const { isPhone, isTablet } = useMediaQueries()

  useEffectOnMount(() => {
    setHasSeenPromo(true)
    trackInternalEvent('see_rd_modal')
  })

  const handleOnClose = () => {
    sessionStorage.removeItem('redirect_to_resume_distribution')
    trackInternalEvent('close_rd_modal')
    onClose()
  }

  return isPhone ? (
    <DrawerModal open onClose={handleOnClose}>
      <Image src={RecruitersMobile} />
      <MobileContent>
        <Content triggerDownload={triggerDownload} />
      </MobileContent>
    </DrawerModal>
  ) : (
    <ModalOverlay
      overlayFadeDuration={300}
      contentSlideDuration={300}
      onClick={handleOnClose}
      fullScreen={isPhone}
    >
      <Modal>
        <Container>
          <Content triggerDownload={triggerDownload} />
        </Container>
        <RightModalContainer>
          <Image src={isTablet ? RecruitersTablet : RecruitersDesktop} />
        </RightModalContainer>
        <CloseButton onClick={handleOnClose} />
      </Modal>
    </ModalOverlay>
  )
}
